/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

// Isomorphic: runs in both window and ServiceWorkerGlobalScope.
// Do NOT import React, mobx, window-only APIs, or app stores from this file.

export const SW_LOG_DB_NAME = 'FluxerSwLogs';
export const SW_LOG_DB_VERSION = 1;
const LOGS_STORE = 'logs';
const META_STORE = 'meta';
const ENABLED_KEY = 'enabled';
const MAX_LOGS = 2000;
const TRIM_TRIGGER = 2200;

export type SwLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface SwLogRecord {
	id?: number;
	ts: number;
	level: SwLogLevel;
	source: string;
	message: string;
	data?: unknown;
}

const idbAvailable = (): boolean => typeof indexedDB !== 'undefined';

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
	if (dbPromise) return dbPromise;
	if (!idbAvailable()) {
		return Promise.reject(new Error('IndexedDB unavailable'));
	}
	dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
		let req: IDBOpenDBRequest;
		try {
			req = indexedDB.open(SW_LOG_DB_NAME, SW_LOG_DB_VERSION);
		} catch (err) {
			reject(err);
			return;
		}
		req.onupgradeneeded = () => {
			const db = req.result;
			if (!db.objectStoreNames.contains(LOGS_STORE)) {
				const store = db.createObjectStore(LOGS_STORE, {keyPath: 'id', autoIncrement: true});
				store.createIndex('ts', 'ts');
			}
			if (!db.objectStoreNames.contains(META_STORE)) {
				db.createObjectStore(META_STORE);
			}
		};
		req.onsuccess = () => {
			const db = req.result;
			db.onclose = () => {
				dbPromise = null;
			};
			db.onversionchange = () => {
				try {
					db.close();
				} catch {
					/* ignore */
				}
				dbPromise = null;
			};
			resolve(db);
		};
		req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
		req.onblocked = () => reject(new Error('IndexedDB open blocked'));
	}).catch((err) => {
		dbPromise = null;
		throw err;
	});
	return dbPromise;
};

// Normalize values so they survive structured clone and are readable later.
const serializeData = (value: unknown, depth = 0): unknown => {
	if (value == null) return value;
	const t = typeof value;
	if (t === 'string' || t === 'number' || t === 'boolean') return value;
	if (t === 'bigint') return (value as bigint).toString();
	if (value instanceof Error) {
		return {__type: 'Error', name: value.name, message: value.message, stack: value.stack};
	}
	if (value instanceof Date) return value.toISOString();
	if (depth > 4) return '[truncated]';
	if (Array.isArray(value)) {
		return value.slice(0, 50).map((item) => serializeData(item, depth + 1));
	}
	if (t === 'object') {
		const out: Record<string, unknown> = {};
		let count = 0;
		for (const key of Object.keys(value as Record<string, unknown>)) {
			if (count >= 50) {
				out['__truncated'] = true;
				break;
			}
			try {
				out[key] = serializeData((value as Record<string, unknown>)[key], depth + 1);
			} catch {
				out[key] = '[unserializable]';
			}
			count += 1;
		}
		return out;
	}
	return `[${t}]`;
};

export async function appendSwLog(
	level: SwLogLevel,
	source: string,
	message: string,
	data?: unknown,
): Promise<void> {
	if (!idbAvailable()) return;
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return;
	}
	const record: SwLogRecord = {
		ts: Date.now(),
		level,
		source,
		message,
		data: data === undefined ? undefined : serializeData(data),
	};
	try {
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(LOGS_STORE, 'readwrite');
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
			tx.objectStore(LOGS_STORE).add(record);
		});
	} catch {
		return;
	}
	// Trim opportunistically in a later task so the write returns quickly.
	void scheduleTrim();
}

let trimScheduled = false;
const scheduleTrim = async (): Promise<void> => {
	if (trimScheduled) return;
	trimScheduled = true;
	try {
		await trimIfNeeded();
	} finally {
		trimScheduled = false;
	}
};

async function trimIfNeeded(): Promise<void> {
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return;
	}
	let count = 0;
	try {
		count = await new Promise<number>((resolve, reject) => {
			const tx = db.transaction(LOGS_STORE, 'readonly');
			const req = tx.objectStore(LOGS_STORE).count();
			req.onsuccess = () => resolve(req.result);
			req.onerror = () => reject(req.error);
		});
	} catch {
		return;
	}
	if (count < TRIM_TRIGGER) return;
	const toDelete = count - MAX_LOGS;
	try {
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(LOGS_STORE, 'readwrite');
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
			const store = tx.objectStore(LOGS_STORE);
			const cursorReq = store.index('ts').openCursor();
			let deleted = 0;
			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result;
				if (!cursor || deleted >= toDelete) return;
				cursor.delete();
				deleted += 1;
				cursor.continue();
			};
			cursorReq.onerror = () => reject(cursorReq.error);
		});
	} catch {
		/* ignore */
	}
}

export async function getAllSwLogs(): Promise<Array<SwLogRecord>> {
	if (!idbAvailable()) return [];
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return [];
	}
	return new Promise<Array<SwLogRecord>>((resolve, reject) => {
		const tx = db.transaction(LOGS_STORE, 'readonly');
		const req = tx.objectStore(LOGS_STORE).index('ts').getAll();
		req.onsuccess = () => {
			const rows = (req.result as Array<SwLogRecord>) ?? [];
			// newest first
			rows.reverse();
			resolve(rows);
		};
		req.onerror = () => reject(req.error);
	}).catch(() => []);
}

export async function clearSwLogs(): Promise<void> {
	if (!idbAvailable()) return;
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return;
	}
	await new Promise<void>((resolve, reject) => {
		const tx = db.transaction(LOGS_STORE, 'readwrite');
		tx.oncomplete = () => resolve();
		tx.onerror = () => reject(tx.error);
		tx.objectStore(LOGS_STORE).clear();
	}).catch(() => undefined);
}

export async function isSwLoggingEnabled(): Promise<boolean> {
	if (!idbAvailable()) return false;
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return false;
	}
	return new Promise<boolean>((resolve) => {
		const tx = db.transaction(META_STORE, 'readonly');
		const req = tx.objectStore(META_STORE).get(ENABLED_KEY);
		req.onsuccess = () => resolve(req.result === true);
		req.onerror = () => resolve(false);
	});
}

export async function setSwLoggingEnabled(enabled: boolean): Promise<void> {
	if (!idbAvailable()) return;
	let db: IDBDatabase;
	try {
		db = await openDb();
	} catch {
		return;
	}
	await new Promise<void>((resolve) => {
		const tx = db.transaction(META_STORE, 'readwrite');
		tx.oncomplete = () => resolve();
		tx.onerror = () => resolve();
		tx.onabort = () => resolve();
		tx.objectStore(META_STORE).put(enabled, ENABLED_KEY);
	});
}

export const SW_LOG_SET_ENABLED_MESSAGE = 'SW_LOG_SET_ENABLED';
